import React, { useEffect, useState } from "react";
import { useAuthStore } from "../store/authStore";

const API_BASE =
  process.env.NODE_ENV === "production"

    ? "https://operacrm.com"
    : "https://operacrm.com";


export default function TwoFASettings() {
  const { isAuthenticated } = useAuthStore();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [qrImage, setQrImage] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setError("You must be logged in to manage Two-Factor Authentication.");
      setLoading(false);
      return;
    }
    check2FAStatus();
  }, [isAuthenticated]);

  function getSessionIdFromCookie() {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith("session_id="))
      ?.split("=")[1];
  }

  // ✅ Check 2FA status
  async function check2FAStatus() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/v1/user/2fa_status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Status fetch failed: ${res.status}`);
      const data = await res.json();

      const statusData = data.result || data;
      setEnabled(!!statusData.enabled);
    } catch (err: any) {
      console.error("Status check error:", err);
      setError(err.message || "Unable to fetch 2FA status.");
    } finally {
      setLoading(false);
    }
  }

  // ✅ Enable 2FA
  async function handleEnable() {
    setError("");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE}/api/v1/user/enable_2fa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        // body: JSON.stringify({}),
        body: JSON.stringify({
          session_id: getSessionIdFromCookie(),
        }),
      });
      if (!res.ok) throw new Error(`Enable fetch failed: ${res.status}`);
      const data = await res.json();

      const responseData = data.result || data;

      if (responseData.error || data.error) {
        setError(responseData.error || data.error);
        return;
      }

      const provisioningUri =
        responseData.provisioning_uri || data.provisioning_uri;

      if (provisioningUri) {
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(
          provisioningUri
        )}&size=200x200`;
        setQrImage(qrUrl);
        setShowModal(true);
        setMessage("Scan this QR code using your authenticator app.");
      } else {
        setError("Failed to generate QR code.");
      }
    } catch (err: any) {
      console.error("Enable error:", err);
      setError(err.message || "Server connection error.");
    }
  }

  // ✅ Verify OTP & enable 2FA
  async function verifyAndEnable() {
    if (!otpCode || otpCode.length !== 6) {
      setError("Please enter a valid 6-digit code.");
      return;
    }

    setError("");
    setMessage("");

    try {
      const res = await fetch(`${API_BASE}/api/v1/user/verify_and_enable_2fa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        // body: JSON.stringify({ code: otpCode }),
        body: JSON.stringify({
          code: otpCode,
          session_id: getSessionIdFromCookie(),
        }),
      });

      const data = await res.json();
      console.log("ankush", data);
      // Handle both direct response and JSON-RPC wrapped response
      const responseData = data.result || data;
      const isSuccess = responseData.success || data.success;
      const errorMsg = responseData.error || data.error;
      console.log("data.result", data.result);
      console.log("errorMsg", errorMsg);
      if (isSuccess) {
        setEnabled(true);
        setQrImage("");
        setOtpCode("");
        setShowModal(false);
        setMessage("Two-Factor Authentication enabled successfully!");
        setTimeout(() => {
          useAuthStore.getState().logout(); // clear session + zp
          window.location.href = "/login";
        }, 5000);

        await check2FAStatus();
      } else {
        setError(errorMsg || "Invalid code.");
      }
    } catch (err: any) {
      console.error("Verify error:", err);
      setError(err.message || "Network error.");
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="max-w-lg mx-auto mt-10 bg-white shadow-lg rounded-2xl p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          Security Settings
        </h2>
        <p className="text-red-600 mb-4">
          {error || "You must be logged in to access 2FA settings."}
        </p>
        <button
          onClick={() => (window.location.href = "/login")}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
        >
          Login
        </button>
      </div>
    );
  }

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">
        Two-Factor Authentication (2FA)
      </h2>
      <p className="text-gray-600 mb-6">
        Manage your Two-Factor Authentication settings for extra account
        security.
      </p>

      {message && <p className="text-green-600 font-medium mb-4">{message}</p>}
      {error && <p className="text-red-600 font-medium mb-4">{error}</p>}

      {/* ✅ Show message if enabled */}
      {enabled === true ? (
        <div className="text-center space-y-3">
          <p className="text-gray-700 font-medium">
            ✅ Two-Factor Authentication is currently activated.
          </p>
        </div>
      ) : (
        // ✅ Show "Enable 2FA" button only if NOT enabled
        <div className="text-center space-y-4">
          {!qrImage && (
            <button
              onClick={handleEnable}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all"
            >
              Enable 2FA
            </button>
          )}
        </div>
      )}

      {/* ✅ Modal for QR & Code Verification */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="mx-4 w-full max-w-sm space-y-4 rounded-xl bg-white p-6 shadow-xl sm:p-8">
            <h3 className="text-lg font-semibold text-gray-800">
              Two-Factor Verification
            </h3>
            <p className="text-gray-600 text-sm">
              Scan this QR code or enter the 6-digit code from your
              authenticator app:
            </p>
            <img
              src={qrImage}
              alt="QR Code 2FA"
              className="mx-auto w-40 h-40 border rounded-lg"
            />
            <input
              type="text"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="6-digit code"
              maxLength={6}
              className="w-full px-4 py-3 border rounded-lg text-center text-lg tracking-widest"
            />
            <button
              onClick={verifyAndEnable}
              disabled={otpCode.length !== 6}
              className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            >
              Verify and Enable
            </button>
            <button
              onClick={() => {
                setShowModal(false);
                setQrImage("");
                setOtpCode("");
              }}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
